import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { actionTones } from '../constants/actionTones';
import { useAuth } from '../../contexts/AuthContext';
import {
  Clock, Users, Calendar, Home, AlertCircle, Search, X, ChevronLeft, FileText, Loader, Shield, UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../services/api';
import useAcademyScope from '../useAcademyScope';
import { resolveMediaUrl, feeUnit } from '../../utils/helpers';
import { paymentsAPI } from '../../payments/services/PaymentApi';
import FlyerModal from '../components/FlyerModal';
import ProgramOverviewPanel from './program/ProgramOverviewPanel';
import ProgramSchedulePanel from './program/ProgramSchedulePanel';
import ProgramSquadsPanel from './program/ProgramSquadsPanel';
import ProgramRosterPanel from './program/ProgramRosterPanel';

// ─── Mock data (fallback only — used for managed players until enrollment API is ready) ──
const MOCK_PROGRAMS = [
  {
    id: 1,
    name: 'Junior Development Program',
    description:
      'Comprehensive tennis development for young players aged 8–14. Focus on fundamentals, match play, and sportsmanship.',
    sport: 'Tennis',
    sportTypeId: 5,
    level: 'beginner',
    ageGroup: 'Ages 8–14',
    price: '$180/month',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    timeFrom: '08:00',
    timeTo: '09:30',
    registrationDueDate: '2026-05-25',
    lateFee: '$25',
    image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=600',
    registrationOpen: true,
    enrollments: [
      { id: 1, name: 'Emma Johnson', type: 'self', registeredOn: '2026-04-15', paid: true },
      { id: 2, name: 'Liam Parker', type: 'managed', managedBy: 'Sarah Parker', registeredOn: '2026-04-16', paid: true },
      { id: 3, name: 'Ava Chen', type: 'managed', managedBy: 'Wei Chen', registeredOn: '2026-04-20', paid: false },
    ],
    classes: [
      {
        id: 101,
        name: 'Monday/Wednesday AM',
        coachName: 'Coach Maria',
        recurrence: 'Mon/Wed',
        time: '08:00',
        duration: 90,
        facility: 'Court A',
        enrolled: 10,
        maxCapacity: 14,
        startDate: '2026-06-01',
        endDate: '2026-08-31',
      },
      {
        id: 102,
        name: 'Tuesday/Thursday PM',
        coachName: 'Coach James',
        recurrence: 'Tue/Thu',
        time: '16:00',
        duration: 90,
        facility: 'Court B',
        enrolled: 12,
        maxCapacity: 14,
        startDate: '2026-06-01',
        endDate: '2026-08-31',
      },
    ],
  },
  {
    id: 2,
    name: 'Adult Beginner Program',
    description:
      'Start your tennis journey with patient, structured coaching. No experience needed.',
    sport: 'Tennis',
    sportTypeId: 5,
    level: 'beginner',
    ageGroup: 'Adults 18+',
    price: '$150/month',
    startDate: '2026-06-07',
    endDate: '2026-09-27',
    timeFrom: '09:00',
    timeTo: '10:30',
    registrationDueDate: '2026-06-01',
    lateFee: '$15',
    image: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=600',
    registrationOpen: true,
    enrollments: [
      { id: 4, name: 'John Davis', type: 'self', registeredOn: '2026-05-01', paid: true },
    ],
    classes: [
      {
        id: 201,
        name: 'Saturday Morning',
        coachName: 'Coach Alex',
        recurrence: 'Sat',
        time: '09:00',
        duration: 90,
        facility: 'Court C',
        enrolled: 8,
        maxCapacity: 12,
        startDate: '2026-06-01',
        endDate: '2026-08-31',
      },
    ],
  },
  {
    id: 3,
    name: 'Advanced Competitive Program',
    description:
      'High-intensity training for competitive players. Tournament preparation and match strategy.',
    sport: 'Tennis',
    sportTypeId: 5,
    level: 'advanced',
    ageGroup: 'Ages 15+',
    price: '$250/month',
    startDate: '2026-06-01',
    endDate: '2026-11-30',
    timeFrom: '06:30',
    timeTo: '08:30',
    registrationDueDate: '2026-05-28',
    lateFee: '$50',
    image: 'https://images.unsplash.com/photo-1519766304817-4f37bba85e1c?w=600',
    registrationOpen: false,
    enrollments: [],
    classes: [
      {
        id: 301,
        name: 'Weekday Intensive',
        coachName: 'Coach Maria',
        recurrence: 'Mon/Tue/Thu/Fri',
        time: '06:30',
        duration: 120,
        facility: 'Courts A & B',
        enrolled: 8,
        maxCapacity: 10,
        startDate: '2026-06-01',
        endDate: '2026-08-31',
      },
      {
        id: 302,
        name: 'Weekend Match Play',
        coachName: 'Coach James',
        recurrence: 'Sat/Sun',
        time: '08:00',
        duration: 180,
        facility: 'All Courts',
        enrolled: 8,
        maxCapacity: 10,
        startDate: '2026-06-01',
        endDate: '2026-08-31',
      },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getLevelColor = (level) => {
  switch (level) {
    case 'beginner':     return { bg: 'bg-green-100', text: 'text-green-700' };
    case 'intermediate': return { bg: 'bg-yellow-100', text: 'text-yellow-700' };
    case 'advanced':     return { bg: 'bg-red-100', text: 'text-red-700' };
    default:             return { bg: 'bg-gray-100', text: 'text-gray-600' };
  }
};

const formatTime = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Cards are wide and two to a row, so twelve is six rows — about a screen and a
// half, which is enough to feel like a list without becoming a scroll.
const PAGE_SIZE = 12;

// ─── Sub-tabs ─────────────────────────────────────────────────────────────────
const PROGRAM_SUB_TABS = [
  { id: 'overview',  label: 'Overview', icon: Home },
  { id: 'squads',    label: 'Squads',   icon: Shield },
  { id: 'schedule',  label: 'Schedule', icon: Calendar },
  { id: 'enrolled',  label: 'Enrolled', icon: Users },
];

/**
 * Register is not a tab, and no longer a view either.
 *
 * It used to be both: a `view=register` panel holding a four-step form. That
 * form moved to /checkout, which collects the registrant, the answers and the
 * signature itself — but the links kept pointing at the view, and nothing
 * renders it any more. Clicking Register set a URL parameter and left the body
 * of the page blank, which reads as a dead button.
 *
 * Register now starts checkout directly, the same call Overview's own button
 * makes. There is one way to register and it is the one that works.
 */
const subTabsFor = () => PROGRAM_SUB_TABS;

// ─── Main Component ────────────────────────────────────────────────────────────
const ProgramsTab = ({ academyId, academy = {}, userRole, currentUserId = 1 }) => {
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const effectiveRole = userRole || academy?.userRole || 'member';
  // Managing a program is a capability, not a job title. Owner/admin hold every
  // capability implicitly; everyone else needs the explicit grant — the same
  // rule the API enforces, so the UI stops offering buttons that 403.
  const canManagePrograms = effectiveRole === 'owner' || effectiveRole === 'admin'
    || (academy?.myCapabilities || []).includes('manage_programs');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tone = actionTones(isDark);
  const scope = useAcademyScope(academy);

  // Open play is not registered for, it is booked a night at a time — and the
  // Open Play tab is where the sessions and their dates actually are. Sending
  // someone into the term enrolment flow instead asks them to sign up for a run
  // that does not exist.
  //
  // Keyed on the offering flag rather than programType === 'open_play', which
  // is what offeringPresets asks callers to do, and which is also the correct
  // test: open play and meetups are precisely the presets that sell a single
  // occurrence, and precisely the two the Open Play tab lists.
  const soldByTheNight = (program) => program?.offering?.registrationScope === 'occurrence';
  const openPlayHref = () => `${scope.home()}?tab=play`;

  // URL-driven navigation state
  const selectedProgramId = searchParams.get('program') || null;
  const programTab        = searchParams.get('view')    || 'overview';
  const cameFromReports   = searchParams.get('from') === 'reports';
  const backToReports = () =>
    navigate(`/academy/${searchParams.get('fromAcademy') || academyId}?tab=settings&settings=manage-reports`);
  const view              = selectedProgramId ? 'detail' : 'list';

  const openProgram = (programId) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('program', programId);
    next.set('view', 'overview');
    return next;
  });

  const closeProgram = () => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.delete('program');
    next.delete('view');
    return next;
  });

  const setProgramTab = (tab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('view', tab);
    // `step` belongs to the register view. Leaving it behind would drop you
    // back in at step 5 the next time you opened Register.
    // No step to carry: registering is done on /checkout now.
    next.delete('step');
    return next;
  });

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPast, setShowPast] = useState(false);
  // The page on screen, and what the server said about the rest of them. The
  // filters are the server's job now, so `programs` holds exactly one page and
  // the totals below have to come back with it — the list can no longer count
  // itself.
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  // Every sport the academy runs, for the dropdown. Sent with the page rather
  // than derived from it: derived, the dropdown would only ever offer the
  // sports that happened to land on the page you were looking at.
  const [sportOptions, setSportOptions] = useState([]);
  // The program being registered for. Held separately from the URL-selected
  // program so the list view can open the modal in place, without navigating
  // into the detail view first.
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [flyerTarget, setFlyerTarget] = useState(null);
  // Squad dive-in state — when set, program nav collapses and squad gets full stage
  const [openSquad, setOpenSquad] = useState(null);

  /**
   * One page of programmes, with the filters applied by the server.
   *
   * useCallback because the effect below depends on it: every filter is part of
   * the request now, so the list has to be refetched when any of them changes
   * rather than re-filtered in place.
   */
  const loadPrograms = useCallback(() => {
    if (!academyId) return;
    setLoading(true);
    academyAPI.programs.list(academyId, {
      page, limit: PAGE_SIZE, search: searchQuery.trim(), level: levelFilter,
      sport: sportFilter, showPast,
    })
      .then((res) => {
        setPrograms(Array.isArray(res?.data) ? res.data : []);
        setPagination(res?.pagination ?? null);
        // Only when the server sent some. A failed or empty page must not wipe
        // the dropdown the reader is filtering with.
        if (res?.filters?.sports?.length) setSportOptions(res.filters.sports);
      })
      .catch(() => { setPrograms([]); setPagination(null); })
      .finally(() => setLoading(false));
  }, [academyId, page, searchQuery, levelFilter, sportFilter, showPast]);

  // Enrolment state has to come from the server, not just from what happened in
  // this session — otherwise a refresh makes programs you're already in look
  // available again.
  const loadMyRegistrations = () => {
    if (!academyId) return;
    academyAPI.programs.myAcademyRegistrations(academyId)
      .then(r => setMyEnrollments(r?.registrations || []))
      .catch(() => setMyEnrollments([]));
  };

  // Refetches whenever the page or any filter changes, because all of them are
  // now part of the request. Debounced, so typing a search does not fire a
  // request per keystroke.
  useEffect(() => {
    const t = setTimeout(loadPrograms, searchQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadPrograms, searchQuery]);

  useEffect(() => { loadMyRegistrations(); }, [academyId]);

  // Back to page one whenever the result set changes underneath. Without this,
  // narrowing a search while on page 6 asks for a page the new result set does
  // not have, and the screen reads as "no programs found".
  useEffect(() => { setPage(1); }, [academyId, searchQuery, levelFilter, sportFilter, showPast]);

  // Reset squad dive-in when program changes or user switches away from squads tab
  useEffect(() => { setOpenSquad(null); }, [selectedProgramId, programTab]);

  const selectedProgram = programs.find((p) => p.id === selectedProgramId) || null;

  /**
   * Register goes straight to checkout.
   *
   * The old route opened a four-step form here — registrant, details,
   * agreements, review — and only then handed off to pay. Checkout collects all
   * four itself now, so keeping the form would ask the same questions twice.
   *
   * An ORDER is raised first because that is what checkout is pointed at, and
   * it carries a pending_payload saying what to create. Nothing is enrolled
   * yet: the enrolment is written when the payment succeeds, which is what
   * makes closing the tab or pressing back cost nothing.
   */
  const [raising, setRaising] = useState(false);

  const startProgramCheckout = async (program, registrantUserId) => {
    try {
      const order = await paymentsAPI.startBooking({
        items: [{
          subjectType: 'program',
          subjectId: String(program.id),
          /* WHO IT IS FOR, NAMED HERE — not guessed later.
           *
           * The server refuses a line that does not say. It used to fall back
           * to the payer, which is the only answer available when nobody has
           * been asked, and the one-live-order rule then matched a PAID order
           * against that guess: a parent buying for their child was handed an
           * order they had paid for themselves. Null means "my account", which
           * is a real answer rather than an absent one. */
          playerId: registrantUserId ?? null,
        }],
        // Raise the order even when there is nothing to charge. A programme
        // with no price — or one a membership or discount brings to nothing —
        // still needs somewhere to hang the registrant, the answers and the
        // signature, because none of that exists until the order settles.
        allowFree: true,
        // What to create once this is paid. Checkout fills in the registrant,
        // the answers and the signature; nothing is enrolled before then.
        pendingPayload: {
          type:      'program',
          academyId,
          programId: String(program.id),
        },
      });
      const base = `${window.location.pathname}?tab=programs&program=${program.id}`;
      /* Same reason as Open Play's Book: an order handed back rather than
       * raised is an existing enrolment, and backing out of checkout must not
       * withdraw it. See PaymentCheckoutPage's goBack. */
      navigate(`/checkout?order=${order.id}`
        + (order.reused ? '&reused=1' : '&raised=1')
        + `&from=${encodeURIComponent(`${base}&view=overview`)}`
        + `&done=${encodeURIComponent(`${base}&view=enrolled`)}`
        + `&academy=${encodeURIComponent(academyId)}`
        + `&title=${encodeURIComponent(program?.name || 'Programme')}`);
    } catch (err) {
      toast.error(err?.message || 'Could not start checkout');
    }
  };

  /**
   * Register, free or not.
   *
   * One route for both. What a place costs is a property of the basket and the
   * person — a priced programme can come to nothing for a member — so the
   * decision belongs to checkout, which is the only thing that knows the
   * total. It drops the payment step when there is nothing to pay and asks
   * everything else exactly as before.
   */
  /* STRAIGHT TO CHECKOUT.
   *
   * Register used to stop at a "who is this for?" dialog, which asked the same
   * question checkout asks on its own first step — two screens for one answer,
   * and the first one stood between the button and the thing it said it did.
   *
   * The place is held for the payer and the registrant is named at checkout;
   * changing it there moves the enrolment with it, so the later answer is the
   * one that counts and nothing is lost by not asking twice.
   *
   * ONE PRESS, ONE ORDER. The dialog was also what stopped a second press
   * landing while the first was still in flight — the server hands back the
   * same live order rather than billing twice, but two navigations racing is a
   * screen that flickers and lands somewhere unexpected. */
  const registerFor = async (program) => {
    if (raising) return;
    setRaising(true);
    try {
      await startProgramCheckout(program, null);
    } finally {
      setRaising(false);
    }
  };

  // Seats left for a program, or null when capacity is unlimited.
  const seatsLeft = (program) => program.maxParticipants != null
    ? Math.max(0, program.maxParticipants - (program.enrollmentCount ?? 0))
    : null;

  /**
   * Registration is offered when it's open and there's room.
   *
   * ─── EXCEPT AT THE FRONT DESK ───────────────────────────────────────────
   *
   * Closing registration is aimed at the public, not at the venue. A coach
   * taking a late entry at the counter is the usual reason a programme would
   * otherwise be reopened to the whole world for one person, so somebody who
   * can manage the programme keeps the button on a closed one.
   *
   * This only shows the button — the rule itself lives on the server, which
   * refuses a closed programme unless the caller holds `manage_programs` at
   * that academy. Hiding a button is a courtesy; it is not a permission.
   *
   * A FULL programme still stops everyone. Overriding a closed door is a
   * decision the desk is entitled to make; putting somebody in a room with no
   * space is not, and quietly overselling is worse than saying no.
   */
  const canRegister = (program) =>
    (program.registrationOpen || canManagePrograms) && seatsLeft(program) !== 0;

  const isEnrolled = (programId) =>
    myEnrollments.some((e) => e.programId === programId && (e.userIds || []).includes(user?.id));


  // null = unlimited (no maxParticipants set), number = seats left
  const seatsAvailable = selectedProgram ? seatsLeft(selectedProgram) : null;

  // ── Filtering ──────────────────────────────────────────────────────────────
  //
  // Done by the server. `programs` is one page, so filtering it here would only
  // ever search the twelve rows on screen — a search would miss every match on
  // another page and report "No programs found", which is worse than not
  // offering the search at all.
  const uniqueSports = ['all', ...sportOptions];
  const anyFilterActive = searchQuery.trim() || levelFilter !== 'all' || sportFilter !== 'all' || showPast;


  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  const renderListView = () => loading ? (
    <div className="flex justify-center py-20">
      <Loader className={`w-8 h-8 animate-spin ${theme.link}`} />
    </div>
  ) : (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className={`relative flex items-center flex-1 min-w-[180px] max-w-xs border ${theme.border} rounded-lg overflow-hidden`}>
          <Search size={14} className={`absolute left-3 ${theme.textMuted} pointer-events-none`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search programs…"
            className={`w-full pl-8 pr-7 py-2 text-sm ${theme.input} bg-transparent outline-none`}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className={`absolute right-2 ${theme.textMuted}`}>
              <X size={13} />
            </button>
          )}
        </div>

        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className={`px-3 py-2 text-sm border ${theme.border} rounded-lg ${theme.input} bg-transparent outline-none focus:ring-2 focus:ring-current`}
        >
          <option value="all">All Levels</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>

        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className={`px-3 py-2 text-sm border ${theme.border} rounded-lg ${theme.input} bg-transparent outline-none focus:ring-2 focus:ring-current`}
        >
          {uniqueSports.map((s) => (
            <option key={s} value={s}>{s === 'all' ? 'All Sports' : s}</option>
          ))}
        </select>

        <label className={`flex items-center gap-2 text-sm ${theme.textSecondary} cursor-pointer select-none`}>
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            className={`w-4 h-4 rounded border-gray-300 ${theme.link} focus:ring-current`}
          />
          Show past
        </label>

        {anyFilterActive && (
          <button
            onClick={() => { setSearchQuery(''); setLevelFilter('all'); setSportFilter('all'); setShowPast(false); }}
            className={`text-xs ${theme.link} hover:underline whitespace-nowrap`}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Program grid */}
      {programs.length === 0 ? (
        <div className={`${theme.card} border ${theme.border} text-center p-12 rounded-xl`}>
          <Users size={48} className={`mx-auto mb-3 ${theme.textMuted}`} />
          <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No programs found</h5>
          <p className={theme.textSecondary}>Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {programs.map((program) => {
            const levelColor = getLevelColor(program.level);
            const sessionCount = program.classes ? program.classes.length : 0;
            const enrolled = isEnrolled(program.id);
            return (
              <div
                key={program.id}
                className={`${theme.card} rounded-xl border ${theme.border} overflow-hidden flex flex-col`}
              >
                <div className="flex">
                  {/* Image */}
                  <div className="relative shrink-0 min-h-[180px]" style={{ width: '35%' }}>
                    {program.imageUrl ? (
                      <img
                        src={resolveMediaUrl(program.imageUrl)}
                        alt={program.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`absolute inset-0 ${theme.backgroundSecondary} flex items-center justify-center`}>
                        <Users size={32} className={theme.link} />
                      </div>
                    )}
                    {program.sportName && (
                      <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-semibold rounded ${theme.tabActive}`}>
                        {program.sportName}
                      </span>
                    )}
                    {/* Omitted rather than shown empty when a programme has no
                        level — the same as the sport badge above. level is
                        nullable (the create form never asked for one), and
                        calling charAt on it threw inside the list's map, which
                        took the whole tab down for every programme rather than
                        spoiling one card. */}
                    {program.level && (
                      <span className={`absolute bottom-2 left-2 px-2 py-0.5 text-xs font-semibold rounded ${levelColor.bg} ${levelColor.text}`}>
                        {program.level.charAt(0).toUpperCase() + program.level.slice(1)}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 flex flex-col gap-2 min-w-0">
                    <div>
                      <h4 className={`text-base font-bold ${theme.text} leading-snug`}>{program.name}</h4>
                      <p
                        className={`text-xs ${theme.textSecondary} mt-1`}
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {program.description}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {program.ageGroup && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          {program.ageGroup}
                        </span>
                      )}
                      {/* WHAT IT COSTS COMES FROM THE PRICE TAG.
                        * `fee` was a column on the programme that was read and
                        * written and never charged; the tag is what checkout
                        * actually bills, so showing anything else would be
                        * quoting a number nobody is asked to pay. Absent when
                        * the programme is unpriced or its tag is withdrawn. */}
                      {program.price?.amount != null && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${theme.backgroundSecondary} ${theme.link}`}>
                          ${program.price.amount}{feeUnit(program)}
                        </span>
                      )}
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        <Clock size={10} />
                        {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'}
                      </span>
                    </div>

                    {(program.startDate || program.endDate) && (
                      <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs ${theme.textSecondary}`}>
                        <span className="flex items-center gap-1">
                          <Calendar size={11} className="flex-shrink-0" />
                          {program.startDate ? formatDate(program.startDate) : '—'}
                          {' – '}
                          {program.endDate ? formatDate(program.endDate) : '—'}
                        </span>
                        {(program.timeFrom || program.timeTo) && (
                          <span className="flex items-center gap-1">
                            <span className={theme.textMuted}>·</span>
                            <Clock size={11} className="flex-shrink-0" />
                            {program.timeFrom ? formatTime(program.timeFrom) : '—'}
                            {' – '}
                            {program.timeTo ? formatTime(program.timeTo) : '—'}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        program.registrationOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {program.registrationOpen ? 'Registration Open' : 'Closed'}
                      </span>
                      {program.registrationDueDate && (
                        <span className={`flex items-center gap-1 text-xs ${theme.textSecondary}`}>
                          <Calendar size={10} />
                          Due {formatDate(program.registrationDueDate)}
                        </span>
                      )}
                      {/* The late fee is two columns on the TAG now — an
                        * amount and how many days before the start it begins —
                        * because a tag is shared and cannot carry one
                        * programme's dates. */}
                      {program.price?.lateFeeAmount != null && (
                        <span className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertCircle size={10} />
                          ${program.price.lateFeeAmount} late fee
                        </span>
                      )}
                    </div>

                    <div className="mt-auto pt-1 flex flex-col gap-1.5">
                      {enrolled ? (
                        // "Enrolled" reports your own status — it must not remove the
                        // ability to register someone else. A parent already in a
                        // program still needs to enrol a newly added managed player.
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="flex-1 text-center px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
                            Enrolled
                          </span>
                          {canRegister(program) && (
                            <button
                              onClick={() => registerFor(program)}
                              className={`flex-1 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-medium whitespace-nowrap transition`}
                              title="Register another player"
                            >
                              Add player
                            </button>
                          )}
                          <button
                            onClick={() => { openProgram(program.id); }}
                            className={`flex-1 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-xs font-medium transition`}
                          >
                            Details
                          </button>
                        </div>
                      ) : canRegister(program) ? (
                        // Register straight from the list — no need to open the
                        // program first. For open play that means the Open Play
                        // tab, and the card carries Register alone: a programme
                        // overview of something sold by the night has nothing to
                        // say that the session list does not say better.
                        <div className="flex flex-wrap items-center gap-1.5">
                          <button
                            onClick={() => (soldByTheNight(program)
                              ? navigate(openPlayHref())
                              : registerFor(program))}
                            className={`flex-1 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-medium transition`}
                          >
                            Register
                          </button>
                          {!soldByTheNight(program) && (
                            <button
                              onClick={() => { openProgram(program.id); }}
                              className={`flex-1 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-xs font-medium transition`}
                            >
                              View Program
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => { openProgram(program.id); }}
                          className={`w-full px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-medium transition`}
                        >
                          View Program
                        </button>
                      )}
                      {program.flyerUrl ? (
                        <a
                          href={program.flyerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition ${tone.flyer}`}
                        >
                          <FileText size={12} />
                          View Flyer
                        </a>
                      ) : (
                        <button
                          onClick={() => setFlyerTarget({ type: 'program', data: program, programName: program.name })}
                          className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-xs font-medium transition`}
                        >
                          <FileText size={12} />
                          View Flyer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ──
          Only when there is more than one page: a single page of results needs
          no controls, and drawing a dead "Page 1 of 1" under every short list
          is noise. The counts come from the server, so they describe every
          programme that matches the filters — not the twelve on screen. */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!pagination.hasPreviousPage}
            aria-label="Previous page"
            className={`p-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-40 ${theme.cardHover} transition`}
          >
            <ChevronLeft size={18} />
          </button>
          <span className={`text-sm ${theme.textSecondary}`}>
            Page {pagination.currentPage} of {pagination.totalPages}
            <span className={`ml-2 ${theme.textMuted}`}>({pagination.totalRecords} programs)</span>
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!pagination.hasNextPage}
            aria-label="Next page"
            className={`p-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-40 ${theme.cardHover} transition`}
          >
            {/* ChevronLeft flipped — the file imports no right-facing chevron,
                and one more icon import for a mirror image is not worth it. */}
            <ChevronLeft size={18} className="rotate-180" />
          </button>
        </div>
      )}
    </div>
  );

  // ── DETAIL VIEW ────────────────────────────────────────────────────────────
  const renderDetailView = () => {
    if (!selectedProgram) return null;

    // True when the user has dived into a squad — the program nav collapses
    const squadMode = programTab === 'squads' && openSquad !== null;

    return (
      <div>
        {/* ── Program nav — collapses smoothly when a squad is opened ── */}
        <div
          style={{
            overflow:   'hidden',
            maxHeight:  squadMode ? 0   : 200,
            opacity:    squadMode ? 0   : 1,
            marginBottom: squadMode ? 0 : 20,
            transition: 'max-height 320ms cubic-bezier(0.4,0,0.2,1), opacity 220ms ease, margin-bottom 320ms ease',
            pointerEvents: squadMode ? 'none' : 'auto',
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            {/* Arriving here from a report is not the same as browsing in, and
                the reader did not choose this program — the way back is to the
                figure they opened, not to the programs list. */}
            {cameFromReports ? (
              <button
                onClick={backToReports}
                className={`flex items-center gap-1 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition`}
              >
                <ChevronLeft size={15} /> Reports
              </button>
            ) : (
              <button
                onClick={closeProgram}
                className={`flex items-center gap-1 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition`}
              >
                <ChevronLeft size={15} /> Programs
              </button>
            )}
            <h2 className={`text-xl font-bold ${theme.text} truncate`}>{selectedProgram.name}</h2>
          </div>

          <div className={`flex gap-1 border-b ${theme.border} overflow-x-auto`}>
            {subTabsFor(selectedProgram, programTab, canRegister(selectedProgram)).map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setProgramTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                    programTab === t.id
                      ? `${theme.borderAccent} ${theme.link}`
                      : `border-transparent ${theme.textSecondary} hover:${theme.text}`
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Breadcrumb — morphs in when squad is open ── */}
        <div
          style={{
            overflow:     'hidden',
            maxHeight:    squadMode ? 52  : 0,
            opacity:      squadMode ? 1   : 0,
            marginBottom: squadMode ? 20  : 0,
            transition:   'max-height 320ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease 120ms, margin-bottom 320ms ease',
            pointerEvents: squadMode ? 'auto' : 'none',
          }}
        >
          <div className="flex items-center gap-1.5 text-sm">
            <button
              onClick={() => setOpenSquad(null)}
              className={`flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700 transition`}
            >
              <ChevronLeft size={14} />
              {selectedProgram.name}
            </button>
            <span className={theme.textMuted}>/</span>
            <span className={`font-semibold ${theme.text}`}>{openSquad?.name}</span>
          </div>
        </div>

        {programTab === 'overview' && (
          <ProgramOverviewPanel
            program={selectedProgram}
            enrolled={isEnrolled(selectedProgram.id)}
            seatsAvailable={seatsAvailable}
            onRegister={() => registerFor(selectedProgram)}
          />
        )}
        {programTab === 'squads' && (
          <ProgramSquadsPanel
            program={selectedProgram}
            academyId={academyId}
            userRole={effectiveRole}
            openSquad={openSquad}
            onSquadOpen={setOpenSquad}
            onSquadClose={() => setOpenSquad(null)}
          />
        )}
        {programTab === 'schedule' && (
          <ProgramSchedulePanel program={selectedProgram} userRole={effectiveRole} />
        )}
        {programTab === 'enrolled' && (
          <ProgramRosterPanel
            program={selectedProgram}
            academyId={academyId}
            // Needed to send a Stripe payer back to this exact tab.
            academySlug={academy?.slug || null}
            userRole={effectiveRole}
            canManagePrograms={canManagePrograms}
          />
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {view === 'list' && renderListView()}
      {view === 'detail' && renderDetailView()}

      <FlyerModal
        isOpen={!!flyerTarget}
        onClose={() => setFlyerTarget(null)}
        type={flyerTarget?.type || 'program'}
        data={flyerTarget?.data || {}}
        academy={academy}
        academyId={academyId}
      />
    </div>
  );
};

export default ProgramsTab;
